import type { CancelTokenSource } from 'axios';
import { create } from 'zustand';

import * as metrics from 'lib/metrics';
import type { ServerError } from 'models';
import type { CryptoOperation, CryptoProvider } from 'models/crypto';
import type {
    DepositTrackingRequestProps,
    MeshIntegrationToken,
    MeshWithdrawalAddress,
    WithdrawalSource,
} from 'models/reqres';
import { useCryptoCitizenshipDialogStore } from 'stores/zustand/cryptoCitizenshipDialogStore';
import { handleSnackbarError } from 'stores/zustand/snackbarStore';
import config from 'utils/config';
import { getDepositRequestTrackingProps } from 'utils/depositTracking';
import { fetchSardineSessionKey } from 'utils/sardine';

import {
    createCryptoAccount as createCryptoAccountRequest,
    createCryptoWithdrawal,
    getCryptoAccessToken,
    getCryptoAccountDetails as getCryptoAccountDetailsRequest,
    getMeshLinkToken,
    getMeshWithdrawalAddresses,
    initiateMeshWithdrawal as initiateMeshWithdrawalRequest,
} from '../../transport/domains/crypto';

// The rail vendor for crypto, resolved by callers via
// payments.getEnabledVendorForRail('crypto', ...) and passed into
// createTransfer. Kept out of this store so it stays decoupled from the
// payments store.
type CryptoRailVendor = 'stripe' | 'zerohash' | 'coinbase' | null;

// Non-reactive imperative handles — no component renders off these, so they
// live at module scope rather than in the reactive store state.
let tokenTimer: NodeJS.Timeout | null = null;
let tokenFetch: CancelTokenSource | null = null;

function depositTrackingPropsMatch(
    left: DepositTrackingRequestProps | undefined,
    right: DepositTrackingRequestProps | undefined,
): boolean {
    return left?.deposit_flow_id === right?.deposit_flow_id && left?.frontend_session_id === right?.frontend_session_id;
}

interface CryptoFlowState {
    loading: boolean;
    linkToken: string;
    linkTokenTrackingProps: DepositTrackingRequestProps | undefined;
    meshLinkToken: string;
    meshWalletAddresses: MeshWithdrawalAddress[];
    selectedMeshAddress: MeshWithdrawalAddress | null;
    actions: {
        getCryptoAccountDetails: (
            userId: string,
            onSuc: (token: string) => void,
            onErr: (err: ServerError) => void,
            trackingProps?: DepositTrackingRequestProps,
        ) => void;
        createCryptoAccount: (
            userId: string,
            citizenship: string,
            onSuc: (token: string) => void,
            onErr: (error: ServerError) => void,
        ) => Promise<void>;
        createTransfer: (
            userId: string,
            vendor: CryptoRailVendor,
            onLaunchCrypto: (
                token: string,
                provider: CryptoProvider,
                meshIntegrationTokens?: MeshIntegrationToken[],
            ) => void,
            onSuc?: () => void,
            onErr?: (err: ServerError) => void,
            operation?: CryptoOperation,
        ) => void;
        createZeroHashTransfer: (
            userId: string,
            onLaunchZeroHash: (token: string, provider: CryptoProvider) => void,
            onSuc?: () => void,
            onErr?: (err: ServerError) => void,
            trackingProps?: DepositTrackingRequestProps,
        ) => void;
        createMeshTransfer: (
            userId: string,
            operation: CryptoOperation,
            onLaunchMesh: (
                token: string,
                provider: CryptoProvider,
                meshIntegrationTokens?: MeshIntegrationToken[],
            ) => void,
            onSuc?: () => void,
            onErr?: (err: ServerError) => void,
            trackingProps?: DepositTrackingRequestProps,
        ) => void;
        getMeshWalletAddresses: (
            userId: string,
            authToken: string,
            type: string,
            symbol: string,
            onSuc: (addresses: MeshWithdrawalAddress[]) => void,
            onErr: (err: ServerError) => void,
        ) => void;
        initiateMeshWithdrawal: (
            userId: string,
            walletAddress: string,
            amountCents: number,
            network: string,
            mfaCode: string,
            onSuc: () => void,
            onErr: (err: ServerError) => void,
        ) => Promise<void>;
        cryptoWithdraw: (
            userId: string,
            walletId: string,
            amountCents: number,
            mfaCode: string,
            onSuc: () => void,
            onErr: (err: ServerError) => void,
            source?: WithdrawalSource,
        ) => Promise<void>;
        setSelectedMeshAddress: (address: MeshWithdrawalAddress | null) => void;
        setLoading: (loading: boolean) => void;
        disposeLinkToken: () => void;
        disposeMeshLinkToken: () => void;
        dispose: () => void;
    };
}

export const useCryptoFlowStore = create<CryptoFlowState>()((set, get) => ({
    loading: false,
    linkToken: '',
    linkTokenTrackingProps: undefined,
    meshLinkToken: '',
    meshWalletAddresses: [],
    selectedMeshAddress: null,
    actions: {
        getCryptoAccountDetails: (userId, onSuc, onErr, trackingProps) => {
            if (tokenFetch !== null) return;
            set({ loading: true });
            tokenFetch = getCryptoAccountDetailsRequest(
                userId,
                (status, code) => {
                    if (code !== '' && status != '') {
                        fetchSardineSessionKey('deposit', userId).then((sardineSessionKey) => {
                            getCryptoAccessToken(
                                userId,
                                (token) => {
                                    set({ linkToken: token, linkTokenTrackingProps: trackingProps });
                                    tokenTimer = setInterval(() => {
                                        get().actions.disposeLinkToken();
                                    }, config.tokenExpiration);
                                    onSuc(token);
                                },
                                onErr,
                                undefined,
                                sardineSessionKey || undefined,
                                trackingProps,
                            );
                        });
                    } else {
                        useCryptoCitizenshipDialogStore.getState().actions.setCallbackOperation('link-account');
                        useCryptoCitizenshipDialogStore.getState().actions.open();
                        metrics.recordDepositMetric({
                            step: 'cryptoOpenCitizenShip',
                        });
                    }
                },
                (error) => {
                    onErr(error);
                    handleSnackbarError(error);
                },
                () => {
                    tokenFetch = null;
                    set({ loading: false });
                },
            );
        },

        createCryptoAccount: async (userId, citizenship, onSuc, onErr) => {
            const sardineSessionKey = await fetchSardineSessionKey('deposit', userId);
            createCryptoAccountRequest(
                userId,
                citizenship,
                (status, code) => {
                    if (code !== '' && status != '') {
                        fetchSardineSessionKey('deposit', userId).then((innerSardineSessionKey) => {
                            getCryptoAccessToken(
                                userId,
                                (token) => {
                                    set({ linkToken: token, linkTokenTrackingProps: undefined });
                                    tokenTimer = setInterval(() => {
                                        get().actions.disposeLinkToken();
                                    }, config.tokenExpiration);
                                    onSuc(token);
                                },
                                undefined,
                                undefined,
                                innerSardineSessionKey || undefined,
                            );
                        });
                    }
                },
                onErr,
                () => {
                    tokenFetch = null;
                    set({ loading: false });
                },
                sardineSessionKey || undefined,
            );
        },

        // Routes a crypto transfer to its provider-specific flow. Stripe-crypto
        // does NOT belong here — it has its own redirect-based flow; callers
        // must short-circuit on `vendor === 'stripe'` before invoking this.
        createTransfer: (userId, vendor, onLaunchCrypto, onSuc, onErr, operation = 'deposit') => {
            const trackingProps = operation === 'deposit' ? getDepositRequestTrackingProps() : undefined;

            if (vendor === 'stripe') {
                // A stripe-crypto user shouldn't have hit this code path. Bail
                // loudly rather than silently routing to ZeroHash.
                console.warn('crypto.createTransfer called with stripe vendor; caller must handle stripe separately');
                return;
            }

            if (vendor === 'coinbase') {
                get().actions.createMeshTransfer(userId, operation, onLaunchCrypto, onSuc, onErr, trackingProps);
            } else {
                // ZeroHash is the US fallback (and the only remaining vendor here).
                get().actions.createZeroHashTransfer(userId, onLaunchCrypto, onSuc, onErr, trackingProps);
            }
        },

        createZeroHashTransfer: (userId, onLaunchZeroHash, onSuc, onErr, trackingProps) => {
            const { linkToken, linkTokenTrackingProps } = get();
            if (linkToken !== '' && depositTrackingPropsMatch(linkTokenTrackingProps, trackingProps)) {
                onLaunchZeroHash(linkToken, 'zerohash');
                if (onSuc) onSuc();
                return;
            }
            if (linkToken !== '') {
                get().actions.disposeLinkToken();
            }
            get().actions.getCryptoAccountDetails(
                userId,
                (token) => {
                    onLaunchZeroHash(token, 'zerohash');
                    if (onSuc) onSuc();
                },
                (error) => {
                    onErr?.(error);
                },
                trackingProps,
            );
        },

        createMeshTransfer: (userId, operation = 'deposit', onLaunchMesh, onSuc, onErr, trackingProps) => {
            set({ loading: true });
            getMeshLinkToken(
                userId,
                operation,
                (token, integrationTokens) => {
                    set({ meshLinkToken: token });
                    tokenTimer = setInterval(() => {
                        get().actions.disposeMeshLinkToken();
                    }, config.tokenExpiration);
                    onLaunchMesh(token, 'mesh', integrationTokens);
                    if (onSuc) onSuc();
                },
                (error) => {
                    onErr?.(error);
                    handleSnackbarError(error);
                },
                () => {
                    set({ loading: false });
                },
                trackingProps,
            );
        },

        getMeshWalletAddresses: (userId, authToken, type, symbol, onSuc, onErr) => {
            getMeshWithdrawalAddresses(
                userId,
                { auth_token: authToken, institution_type: type },
                (addresses) => {
                    // Filter addresses by symbol to only show matching networks
                    const filteredAddresses = addresses.filter((addr) => addr.symbol === symbol);
                    set({ meshWalletAddresses: filteredAddresses });
                    onSuc(filteredAddresses);
                },
                (error) => {
                    onErr(error);
                    handleSnackbarError(error);
                },
            );
        },

        initiateMeshWithdrawal: async (userId, walletAddress, amountCents, network, mfaCode, onSuc, onErr) => {
            const sardineSessionKey = await fetchSardineSessionKey('withdrawal', userId);
            initiateMeshWithdrawalRequest(
                userId,
                {
                    wallet_address: walletAddress,
                    amount_cents: amountCents,
                    network,
                    mfa_code: mfaCode,
                    sardine_session_key: sardineSessionKey || undefined,
                },
                () => {
                    onSuc();
                },
                (error) => {
                    onErr(error);
                    handleSnackbarError(error);
                },
            );
        },

        cryptoWithdraw: async (userId, walletId, amountCents, mfaCode, onSuc, onErr, source) => {
            // ZeroHash flow for US users
            const sardineSessionKey = await fetchSardineSessionKey('withdrawal', userId);
            createCryptoWithdrawal(
                userId,
                {
                    crypto_wallet_id: walletId,
                    amount_cents: amountCents,
                    mfa_code: mfaCode,
                    sardine_session_key: sardineSessionKey || undefined,
                    source,
                },
                async (token) => {
                    const createZeroHashHandler = await import('utils/crypto').then((mod) => mod.createZeroHashHandler);
                    createZeroHashHandler(token, 'withdraw');
                    onSuc();
                },
                onErr,
            );
        },

        setSelectedMeshAddress: (address) => set({ selectedMeshAddress: address }),

        setLoading: (loading) => set({ loading }),

        disposeLinkToken: () => {
            if (tokenTimer !== null) {
                clearInterval(tokenTimer);
            }
            set({ linkToken: '', linkTokenTrackingProps: undefined });
        },

        disposeMeshLinkToken: () => {
            if (tokenTimer !== null) {
                clearInterval(tokenTimer);
            }
            set({ meshLinkToken: '' });
        },

        dispose: () => {
            get().actions.disposeLinkToken();
            get().actions.disposeMeshLinkToken();
            if (tokenFetch !== null) {
                tokenFetch.cancel('Aborting link token create request');
                tokenFetch = null;
            }
            set({ loading: false });
        },
    },
}));
